// src/encounter/lib/to-education-request.ts
// Build the PUT body for the rail's Education card (PROD-1272). Education is a SINGLE entity per
// patient (PatientEducation) edited in place — Flutter does `PUT {Patients}/PatientEducation/{id}`
// with `jsonEncode(request)` (patient_data_source_impl.dart:451). The level is a CODED pick from the
// master list (GET /api/Master/Education → {code, display, system}); `educationInformation` is free
// text. Pure: transport + cache live in use-save-education.ts.
import type { PatientEducation } from '@/types/patient-detail-api';

/** A master education-level option (GET /api/Master/Education row). */
export interface EducationLevel {
  code: string;
  display: string;
  system: string;
}

/** The dialog's resolved selection (mirrors education_form.dart). */
export interface EducationFormValue {
  /** Picked level from the master list; null when nothing is chosen. */
  level: EducationLevel | null;
  educationInformation: string;
}

export interface EducationRequest {
  patientId: string;
  body: PatientEducation;
}

/**
 * Resolve the form value into a PUT upsert body. Requires a picked level — Flutter
 * PatientEducation.isValidData is false unless educationCode OR educationDisplay is present
 * (patient_education.dart:52-57), so `educationInformation` alone is not saveable. Round-trips the
 * existing entity `id`. Returns null when no valid level is chosen.
 */
export function buildEducationRequest(
  value: EducationFormValue,
  ctx: { patientId: string; existing?: PatientEducation | null },
): EducationRequest | null {
  const level = value.level;
  if (!level || (!level.code?.trim() && !level.display?.trim())) return null;
  return {
    patientId: ctx.patientId,
    body: {
      id: ctx.existing?.id ?? null,
      patientId: ctx.patientId,
      educationCode: level.code?.trim() || null,
      educationDisplay: level.display?.trim() || null,
      educationSystem: level.system?.trim() || null,
      educationInformation: value.educationInformation.trim() || null,
    },
  };
}
